import { Temporal } from '@js-temporal/polyfill';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';

import { gapsLabel } from '../api/compliance.ts';
import {
  answerShift,
  cancelShift,
  clashLabel,
  confirmShifts,
  MISSING_TO_CONFIRM,
  offerShift,
  placeLabel,
  releaseShift,
  removeShift,
  withdrawOffer,
  type Shift,
} from '../api/crew.ts';
import { toSignLabel } from '../api/safety.ts';
import { unassignTask, type Task } from '../api/tasks.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { Popover } from '../components/popover.tsx';
import { OfferPicker, ShiftForm } from './crew-panel.tsx';
import { dayName, initials, stateWord, zoneName } from './roster-bars.tsx';

// A shift opened on the roster, as the operator's ShiftPopover board draws it (2026-10-26,
// docs/designs/roster-page.md §2): who and what in the head, with its state; the steps from
// published to confirmed; its fields, edited in place; the tasks on it, each with Take off; and the
// actions its state allows in the foot (CB-REQ CRW-11 – CRW-16, OPS-02, JOB-10). Everything the
// shift's row offered before is here: offer, record an answer, withdraw, confirm, pulled out,
// release a held day, cancel, and rebook a cancelled job's shift.

type Asking = 'offer' | 'decline' | 'cancel' | 'release' | null;

const hhmm = (local: string) => local.slice(11, 16);

export function ShiftPopover({
  shift,
  tasks,
  mayManage,
  seesPrices,
  onChanged,
  onClose,
}: {
  shift: Shift;
  /** The roster's tasks, for the times and fill of those on this shift. */
  tasks: readonly Task[];
  mayManage: boolean;
  seesPrices: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const formId = useId();
  const [asking, setAsking] = useState<Asking>(null);
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setAsking(null);
      setReason('');
      await onChanged();
    },
  });
  const s = shift;
  const job = s.bookedForJob;
  // JOB-10 at v1.962: a cancelled job's shift only rebooks, pulls out or cancels.
  const resolving = mayManage && (job?.cancelled ?? false);
  const editable = mayManage && !job?.cancelled;
  const name = s.crewMember?.name ?? 'Unfilled shift';
  const confirmable = s.state === 'accepted' && !s.confirmedAt && s.missingToConfirm.length === 0;
  const zone = s.window.zone;

  const subtitle = [
    `${dayName(s.window.localStart.slice(0, 10))} · ${hhmm(s.window.localStart)} – ${hhmm(s.window.localEnd)} ${zoneName(zone)}`,
    job ? `J-${job.number} — ${job.name}` : s.held ? 'Held day, job to come' : '',
    s.held ? '' : placeLabel(s),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Popover
      title={s.role ? `${name} · ${s.role.name}` : name}
      lead={
        <span className="roster-mono h-9! w-9! text-[13px]!" aria-hidden="true">
          {s.crewMember ? initials(s.crewMember.name) : '—'}
        </span>
      }
      tag={<StateTag shift={s} />}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        mayManage ? (
          <>
            {resolving ? (
              <>
                <Button type="submit" form={formId} disabled={act.isPending}>
                  Rebook
                </Button>
                {s.state === 'offered' || s.state === 'accepted' ? (
                  <Button variant="quiet" onClick={() => setAsking('decline')}>
                    Pulled out…
                  </Button>
                ) : null}
              </>
            ) : null}
            {editable &&
            (s.state === 'draft' || s.state === 'published' || s.state === 'declined') ? (
              <Button onClick={() => setAsking('offer')}>
                {s.state === 'declined' ? 'Offer again…' : 'Offer…'}
              </Button>
            ) : null}
            {editable && s.state === 'offered' ? (
              <>
                <Button
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => answerShift(s.id, { answer: 'accepted' }))}
                >
                  Mark accepted
                </Button>
                <Button variant="quiet" onClick={() => setAsking('decline')}>
                  Mark declined…
                </Button>
                <Button
                  variant="quiet"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => withdrawOffer(s.id))}
                >
                  Withdraw
                </Button>
              </>
            ) : null}
            {editable && confirmable ? (
              <Button
                disabled={act.isPending}
                onClick={() => act.mutate(() => confirmShifts([s.id]))}
              >
                Confirm shift
              </Button>
            ) : null}
            {editable ? (
              <Button type="submit" form={formId} variant="quiet">
                Save changes
              </Button>
            ) : null}
            <span className="flex-1" />
            {editable && s.state === 'accepted' ? (
              <Button variant="quiet" onClick={() => setAsking('decline')}>
                Pulled out…
              </Button>
            ) : null}
            {editable && s.state === 'draft' ? (
              <Button
                variant="quiet"
                disabled={act.isPending}
                onClick={() => act.mutate(() => removeShift(s.id))}
              >
                Remove draft
              </Button>
            ) : editable && s.held ? (
              <Button variant="quiet" onClick={() => setAsking('release')}>
                Release…
              </Button>
            ) : mayManage ? (
              <Button variant="quiet" onClick={() => setAsking('cancel')}>
                Cancel shift…
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      <Steps shift={s} />
      <Flags shift={s} />
      {asking === 'offer' ? (
        <OfferPicker
          shift={s}
          onOffer={(crewMemberId) => act.mutate(() => offerShift(s.id, crewMemberId))}
          onClose={() => setAsking(null)}
          busy={act.isPending}
        />
      ) : null}
      {asking === 'decline' || asking === 'release' ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md bg-tile p-3">
          <div className="min-w-56 flex-1">
            <Field
              label={
                asking === 'release'
                  ? `Why it’s released (${s.crewMember?.name ?? 'they'} will see this)`
                  : 'Why (optional)'
              }
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </div>
          <Button
            disabled={act.isPending || (asking === 'release' && !reason.trim())}
            onClick={() =>
              act.mutate(() =>
                asking === 'release'
                  ? releaseShift(s.id, reason.trim())
                  : answerShift(s.id, { answer: 'declined', reason: reason.trim() || null }),
              )
            }
          >
            {asking === 'release' ? 'Yes, release' : 'Save'}
          </Button>
          <Button variant="quiet" onClick={() => setAsking(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {asking === 'cancel' ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-tile p-3">
          <p className="text-sm">
            {s.crewMember && s.state !== 'declined'
              ? `Cancel this shift? Tell ${s.crewMember.name} yourself — AVenue sends nothing yet.`
              : 'Cancel this shift? It stays as cancelled.'}
          </p>
          <Button disabled={act.isPending} onClick={() => act.mutate(() => cancelShift(s.id))}>
            Yes, cancel it
          </Button>
          <Button variant="quiet" onClick={() => setAsking(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}

      {mayManage ? (
        <ShiftForm
          shift={s}
          seesPrices={seesPrices}
          bare
          formId={formId}
          onDone={() => {
            setSaved(true);
            void onChanged();
          }}
        />
      ) : (
        <ReadOnly shift={s} />
      )}
      {saved ? (
        <p role="status" className="text-sm text-muted">
          Saved.
        </p>
      ) : null}

      <OnThisShift shift={s} tasks={tasks} editable={editable} onChanged={onChanged} />

      {s.state === 'accepted' && s.confirmedAt ? (
        <div className="callout">
          <span className="tag tag-accent shrink-0">Once confirmed</span>
          <span>
            A change to its times, where, the role or the rate tells {s.crewMember?.name ?? 'them'}{' '}
            as your change rules say, and may ask them to read it again.
          </span>
        </div>
      ) : null}
      {job ? (
        <Link to="/jobs/$jobId" params={{ jobId: job.id }} className="link self-start text-sm">
          J-{job.number} — {job.name}
        </Link>
      ) : null}
    </Popover>
  );
}

function StateTag({ shift: s }: { shift: Shift }) {
  if (s.state === 'accepted' && !s.confirmedAt) {
    return <span className="tag tag-accepted">Accepted, office to confirm</span>;
  }
  const cls =
    s.state === 'offered'
      ? 'tag-offered'
      : s.state === 'accepted'
        ? 'tag-accent'
        : s.state === 'declined'
          ? 'tag-accent-2'
          : '';
  return <span className={`tag ${cls}`}>{stateWord(s)}</span>;
}

/** CRW-12 and CRW-16: published, offered, accepted, confirmed — done, the next, or still to come. */
function Steps({ shift: s }: { shift: Shift }) {
  const answered = s.respondedAt
    ? Temporal.Instant.from(s.respondedAt)
        .toZonedDateTimeISO(s.window.zone)
        .toPlainTime()
        .toString({ smallestUnit: 'minute' })
    : null;
  const declined = s.state === 'declined';
  const steps = [
    { name: 'Published', done: !!s.publishedAt },
    { name: 'Offered', done: !!s.offeredAt },
    declined
      ? { name: 'Declined', done: true, blocked: true }
      : {
          name: s.state === 'accepted' && answered ? `Accepted ${answered}` : 'Accepted',
          done: s.state === 'accepted',
        },
    { name: s.confirmedAt ? 'Confirmed' : 'Confirm', done: !!s.confirmedAt },
  ];
  const current = steps.findIndex((x) => !x.done);
  return (
    <ol className="stage" aria-label="Where this shift is">
      {steps.map((step, i) => (
        <li
          key={step.name}
          className="stage-step"
          data-step={
            'blocked' in step && step.blocked
              ? 'blocked'
              : step.done
                ? 'done'
                : i === current
                  ? 'current'
                  : undefined
          }
          aria-current={i === current ? 'step' : undefined}
        >
          <span className="stage-n">{i + 1}</span>
          <span className="stage-name">{step.name}</span>
        </li>
      ))}
    </ol>
  );
}

/** What wants attention: gaps, signatures, clashes, what confirming needs, a change unread. */
function Flags({ shift: s }: { shift: Shift }) {
  const flags: Array<{ text: string; problem: boolean }> = [];
  if (s.bookedForJob?.cancelled) flags.push({ text: 'Job cancelled', problem: true });
  if (s.untasked) flags.push({ text: 'No task', problem: false });
  if (s.held && s.lapsed) flags.push({ text: 'Lapsed — no place given', problem: false });
  else if (s.held && s.toAssign) flags.push({ text: 'Held — place it now', problem: true });
  if (s.missingToConfirm.length > 0 && (s.state === 'accepted' || s.confirmedAt)) {
    flags.push({
      text: `${s.confirmedAt ? 'Missing' : 'Needs'} ${s.missingToConfirm.map((m) => MISSING_TO_CONFIRM[m]).join(', ')}`,
      problem: true,
    });
  }
  if (s.state === 'accepted' && !s.confirmedAt && s.toConfirm) {
    flags.push({ text: 'To confirm now', problem: true });
  }
  if (s.complianceGaps.length > 0) {
    flags.push({ text: `Needs ${gapsLabel(s.complianceGaps)}`, problem: true });
  }
  if (s.safetyGaps.length > 0) {
    flags.push({ text: `To sign: ${toSignLabel(s.safetyGaps)}`, problem: false });
  }
  for (const c of s.clashes) flags.push({ text: `Also on ${clashLabel(c)}`, problem: true });
  if (s.state === 'accepted' && s.changeOutcome === 'notify') {
    flags.push({ text: 'Told of a change — not yet seen', problem: false });
  }
  if (s.state === 'accepted' && s.confirmedAt && !s.acknowledgedAt) {
    flags.push({
      text:
        s.changeOutcome === 'reconfirm'
          ? 'Changed — for them to read again'
          : 'Confirmed · not yet read by them',
      problem: false,
    });
  }
  if (s.state === 'offered' && s.changeOutcome === 'reaccept') {
    flags.push({ text: 'Changed — to accept again', problem: false });
  }
  if (s.state === 'declined' && s.declineReason) {
    flags.push({ text: `Declined: ${s.declineReason}`, problem: false });
  }
  if (flags.length === 0) return null;
  return (
    <p className="flex flex-wrap gap-1.5">
      {flags.map((f) => (
        <span key={f.text} className={`tag ${f.problem ? 'tag-accent-2' : ''}`}>
          {f.text}
        </span>
      ))}
    </p>
  );
}

/** OPS-02 at v1.996: the tasks this shift carries, each with Take off. */
function OnThisShift({
  shift,
  tasks,
  editable,
  onChanged,
}: {
  shift: Shift;
  tasks: readonly Task[];
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const takeOff = useMutation({
    mutationFn: (taskId: string) => unassignTask(taskId, shift.id),
    onSuccess: onChanged,
  });
  return (
    <section className="flex flex-col gap-1.5" aria-label="On this shift">
      <h3 className="section-title pb-0! text-[11px]!">
        On this shift · {shift.tasks.length} {shift.tasks.length === 1 ? 'task' : 'tasks'}
      </h3>
      {shift.tasks.map((ref) => {
        const t = tasks.find((x) => x.id === ref.id);
        return (
          <div
            key={ref.id}
            className="flex items-center gap-2.5 rounded-sm border border-divider px-2.5 py-2 text-[13px]"
          >
            <span
              className="h-2.5 w-2.5 rounded-xs"
              style={{
                background:
                  t && (t.status === 'covered' || t.fill === 'full' || t.fill === 'over')
                    ? 'var(--color-accent-600)'
                    : 'var(--color-accent-200)',
              }}
              aria-hidden="true"
            />
            <span className="flex-1">
              {ref.description}
              {t ? ` · ${hhmm(t.window.localStart)} – ${hhmm(t.window.localEnd)}` : ''}
              {ref.fit !== 'fits' ? (
                <span className="tag tag-accent-2 ml-2">
                  {ref.fit === 'partial' ? 'Partial' : 'Outside'}
                </span>
              ) : null}
            </span>
            {t ? (
              <span className="text-muted">
                {t.filled} of {t.crewNeeded}
              </span>
            ) : null}
            {editable ? (
              <Button
                variant="quiet"
                className="small-btn"
                disabled={takeOff.isPending}
                aria-label={`Take ${shift.crewMember?.name ?? 'this shift'} off ${ref.description}`}
                onClick={() => takeOff.mutate(ref.id)}
              >
                Take off
              </Button>
            ) : null}
          </div>
        );
      })}
      {editable ? (
        <p className="text-xs text-muted">
          To add a task, close this, tick tasks with Select, then click the shift.
        </p>
      ) : null}
      {takeOff.isError ? (
        <p role="alert" className="alert">
          {takeOff.error.message}
        </p>
      ) : null}
    </section>
  );
}

/** For someone who sees shifts but doesn't manage them. */
function ReadOnly({ shift: s }: { shift: Shift }) {
  const rows: Array<[string, string]> = [
    ['Role', s.role?.name ?? 'None'],
    [
      'Booked for',
      s.bookedForJob ? `J-${s.bookedForJob.number} — ${s.bookedForJob.name}` : 'No job',
    ],
    ['Call time', hhmm(s.localCallAt)],
    ['Where', placeLabel(s)],
    ['Dress code', s.dressCode?.name ?? 'None yet'],
    ['Contact', s.contact?.name ?? 'None yet'],
  ];
  return (
    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[8rem_1fr]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
      {s.crewNotes ? (
        <>
          <dt className="text-muted">Crew notes</dt>
          <dd>{s.crewNotes}</dd>
        </>
      ) : null}
    </dl>
  );
}
